import React from 'react';
import { motion } from 'framer-motion';
import './ServicesGrid.css';

const ServicesGrid = () => {
  const services = [
    {
      icon: '</>', 
      title: 'Web Development (Right Sized by Design)',
      description: (
        <>
          We build websites that do more than look professional — they support visibility, credibility, and customer conversion. For some businesses, that means a fast, SEO-ready static site designed to communicate clearly and generate enquiries. For others, it means integrated dashboards, booking systems, portals, or operational tools that support day-to-day workflow and future growth.
        </>
      ),
      features: [
        'Static & dynamic websites',
        'Admin-controlled content systems',
        'Client portals & dashboards',
        'Booking systems, payments & integrations'
      ]
    },
    {
      icon: '📅',
      title: 'Booking & Client Management Systems',
      description: (
        <>
          Purpose-built systems designed around how your business actually runs. From appointment scheduling and availability management to client dashboards and automated notifications, we build tools that bring clarity and structure to your day-to-day operations.
        </>
      ),
      features: [
        'Appointment scheduling',
        'Availability management',
        'Client dashboards',
        'Automated notifications'
      ]
    },
    {
      icon: '⚙️',
      title: 'Business Process Automation',
      description: (
        <>
          We help reduce repetitive admin and manual processes by designing simple, reliable automation into your digital infrastructure. Whether it's handling enquiries, routing tasks, or connecting the tools you already use, our goal is <span className="gradient-text">operational clarity and time savings</span>.
        </>
      ),
      features: [
        'Enquiry handling',
        'Workflow automation',
        'Tool integration',
        'Admin reduction'
      ]
    },
    {
      icon: '🎨',
      title: 'UI / UX & Interaction Design',
      description: (
        <>
          Clean, intentional interfaces designed to feel professional, intuitive, and trustworthy. We focus on clarity, usability, and subtle interaction — avoiding unnecessary complexity while delivering a premium experience across desktop, tablet, and mobile.
        </>
      ),
      features: [
        'Professional interfaces',
        'Intuitive design',
        'Responsive layouts',
        'Premium user experience'
      ]
    },
    {
      icon: '🔍',
      title: 'Built to Strengthen Your Digital Presence',
      description: (
        <>
          Your website should do more than exist — it should actively support how your business grows. Every project is developed with structured page hierarchy, performance optimisation, and foundational SEO configuration to support <span className="gradient-text">search visibility and local discoverability</span>.
        </>
      ),
      features: [
        'Clear messaging aligned with your audience',
        'Conversion-focused layouts',
        'Search-ready content structure',
        'Analytics integration for measurable insight',
        'Scalable foundations for future marketing'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  return (
    <section className="services-grid-section">
      <div className="services-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-badge">
            <span className="badge-icon">🔧</span>
            WHAT WE BUILD
          </div>
          <h2 className="section-title">What We Build — Practical Digital Solutions</h2>
          <p className="section-description">
            We design practical, well-engineered digital solutions that support how your business actually operates. Every system we build is structured around clarity, performance, and <span className="gradient-text">measurable impact</span> — not bloated platforms or unnecessary complexity.
          </p>
        </motion.div>

        <motion.div
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card"
              variants={cardVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
              itemScope
              itemType="https://schema.org/Service"
            >
              <div className="card-glow"></div>
              <div className="card-content">
                <div className="service-icon" aria-label={`${service.title} icon`}>{service.icon}</div>
                <h3 className="service-title" itemProp="name">{service.title}</h3>
                <p className="service-description" itemProp="description">{service.description}</p>
                <ul className="service-features" itemProp="areaServed">
                  {service.features.map((feature, idx) => (
                    <li key={idx} itemProp="offers">
                      <span className="feature-dot">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <meta itemProp="serviceType" content={service.title} />
              <meta itemProp="provider" content="Leviathan Coding Systems" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesGrid;
